import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SectionShell } from "@/pages/SectionShell";
import { LegalTabs } from "./LegalTabs";
import { TextField } from "@/components/Field";
import { EmptyState } from "@/components/EmptyState";
import { useT } from "@/contexts/I18nContext";
import { useCallable } from "@/lib/hooks/useCallable";
import { CALLABLES } from "@/services/callables";
import { TYPE_KEY } from "./documents";
import {
  FAULT_KEY,
  FAULT_TONE,
  faultOf,
  matchesQuery,
  missingTypes,
  sortCoverage,
  totals,
  type CoverageRow,
} from "./coverage";

/**
 * The queue.
 *
 * Not a report. Every row is an institution that cannot start a lesson, and
 * somebody there has a class in front of them. The queue is ordered by whose
 * problem it is and then by how much teaching is stopped, so the afternoon is
 * spent on the school with eleven blocked classes rather than on the studio
 * that has not signed anything and has no groups.
 *
 * Blocked groups are a **count**. Their names are content, and reading a
 * customer's content takes a grant their owner gave — a support screen is not a
 * way around that.
 */
export function CoveragePage() {
  const t = useT();
  const coverage = useCallable<{ rows: CoverageRow[] }>(CALLABLES.getLegalCoverage);
  const [query, setQuery] = useState("");

  /*
    Memoised, not written inline. `?? []` produces a fresh array on every
    render, and three memos keyed on it would recompute the whole estate every
    time somebody types a character into the search box.
  */
  const rows = useMemo(() => coverage.data?.rows ?? [], [coverage.data]);
  const summary = useMemo(() => totals(rows), [rows]);
  const missing = useMemo(() => missingTypes(rows), [rows]);
  const visible = useMemo(
    () => sortCoverage(rows.filter((row) => matchesQuery(row, query))),
    [rows, query]
  );

  return (
    <SectionShell titleKey="legal.coverage.title">
      <LegalTabs />

      <p className="max-w-3xl text-sm text-ink-soft">{t("legal.coverage.intro")}</p>

      {coverage.loading ? (
        <p className="mt-8 text-sm text-ink-muted">{t("common.loading")}</p>
      ) : coverage.failed ? (
        <p className="mt-8 text-sm text-accent">{t("error.network.unavailable")}</p>
      ) : (
        <>
          <dl className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { key: "legal.coverage.institutions", value: summary.institutions },
              { key: "legal.coverage.blockedInstitutions", value: summary.blockedInstitutions },
              { key: "legal.coverage.blockedGroups", value: summary.blockedGroups },
              { key: "legal.coverage.oursCount", value: summary.ours },
            ].map((stat) => (
              <div
                key={stat.key}
                className="rounded-md border border-paper-deep bg-white px-5 py-4"
              >
                <dt className="text-sm text-ink-muted">{t(stat.key)}</dt>
                <dd className="mt-1 font-display text-2xl text-ink">{stat.value}</dd>
              </div>
            ))}
          </dl>

          {missing.length > 0 ? (
            /*
              Named first and named as ours. An institution waiting for a
              document nobody has published cannot do anything about it, and a
              queue that put them in the same list as somebody who has not read
              their email would have support chasing customers about our own
              release backlog.
            */
            <div className="mt-8 rounded-md border border-accent-soft bg-accent-soft px-5 py-4">
              <h2 className="font-display text-base text-ink">{t("legal.coverage.gapsTitle")}</h2>
              <p className="mt-1 text-sm text-ink-soft">{t("legal.coverage.gapsBody")}</p>
              <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink">
                {missing.map((type) => (
                  <li key={type}>{t(TYPE_KEY[type])}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-8 max-w-sm">
            <TextField
              labelKey="legal.coverage.search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>

          <section className="mt-6">
            {visible.length === 0 ? (
              <EmptyState titleKey="legal.coverage.empty" bodyKey="legal.coverage.emptyBody" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <caption className="sr-only">{t("legal.coverage.title")}</caption>
                  <thead>
                    <tr className="border-b border-paper-deep text-left text-ink-muted">
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("legal.coverage.column.institution")}
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("legal.coverage.column.profile")}
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("legal.coverage.column.fault")}
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("legal.coverage.column.outstanding")}
                      </th>
                      <th scope="col" className="px-3 py-2.5 font-medium">
                        {t("legal.coverage.column.blocked")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => {
                      const fault = faultOf(row);
                      return (
                        <tr key={row.institutionId} className="border-b border-paper-warm last:border-0">
                          <td className="px-3 py-3">
                            <Link
                              to={`/institutions/${row.institutionId}`}
                              className="text-ink underline underline-offset-2"
                            >
                              {row.name}
                            </Link>
                          </td>
                          <td className="px-3 py-3 text-ink-muted">
                            {t("legal.coverage.profile", {
                              country: row.country,
                              segment: t(`institutions.segment.${row.segment}`),
                            })}
                          </td>
                          <td className={`px-3 py-3 ${FAULT_TONE[fault]}`}>{t(FAULT_KEY[fault])}</td>
                          <td className="px-3 py-3 text-ink-soft">
                            {row.gaps.length > 0
                              ? row.gaps.map((type) => t(TYPE_KEY[type])).join(", ")
                              : row.outstanding.map((entry) => entry.title).join(", ")}
                          </td>
                          <td className="px-3 py-3 text-ink">
                            {t("legal.coverage.blockedOf", {
                              blocked: row.blockedGroups,
                              active: row.activeGroups,
                            })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </SectionShell>
  );
}
